import {
  ChatMessageWithParts,
  ContentType,
  IncomingSSEMessage,
  TArtifact,
  TMessageContentPart,
} from "../types";

import {
  extractToolCallId,
  resolveContentType,
} from "src/reusable/Chat/ChatStreaming/services/contentTypeUtils";
import {
  canMergeConsecutiveParts,
  isDuplicateUserMessage,
  normalizeContentParts,
} from "src/reusable/Chat/ChatStreaming/services/messageParserUtils";
import { StreamingState } from "src/reusable/Chat/ChatStreaming/stores/StreamingStore";
import { applySourcesMetadata } from "src/reusable/Chat/ChatStreaming/services/sourcesMetadata";
import { useCallback } from "react";
import { useHistory } from "react-router-dom";
import { useUIStore } from "../stores/UIStore";
import { useChatStore } from "../stores/ChatStore";
import { useOnboardingStore } from "../stores/OnboardingStore";

// Module-7 next-action values map to the post-onboarding destination the
// user picked. Anything else (including missing value) leaves the UI put.
const ONBOARDING_NEXT_ACTION_ROUTES: Record<string, string> = {
  new_chat: "/assistant",
  browse_agents: "/assistant/configs",
  view_profile: "/userprofile",
};

const useAtomAssistEventHandlers = () => {
  const { openArtifact } = useUIStore();
  const history = useHistory();

  // Helper function to set artifact and open sidebar - debounced to prevent re-renders during streaming
  const setArtifactAndOpenSidebar = useCallback(
    (artifactId: string) => {
      // Use setTimeout to prevent immediate re-render during streaming
      setTimeout(() => {
        openArtifact(artifactId);
      }, 0);
    },
    [openArtifact],
  );

  const checkForArtifacts = useCallback(
    (artifacts: Record<string, TArtifact>) => {
      const artifactList = Object.values(artifacts);
      const { rightSidebar, preferences } = useUIStore.getState();

      // Check if auto-open is enabled (default: true)
      const autoOpenEnabled = preferences?.autoOpenSidebar !== false;

      // If current artifact is still valid, don't change anything
      if (
        rightSidebar.currentArtifactId &&
        artifacts[rightSidebar.currentArtifactId]
      ) {
        return;
      }

      // If sidebar isn't showing artifacts and there are artifacts, don't auto-open
      // unless it's a background task (which should always notify user)
      if (!rightSidebar.isArtifactItemOpen && artifactList.length > 0) {
        // Check for background task - these should always open
        const backgroundTaskArtifact = artifactList.find(
          (artifact) =>
            artifact?.content?.text?.result &&
            typeof artifact.content.text.result === "object" &&
            artifact.content.text.result.type === "background_task",
        );

        if (backgroundTaskArtifact && autoOpenEnabled) {
          setArtifactAndOpenSidebar(backgroundTaskArtifact.id);
        }
        return;
      }

      // Only auto-select the first artifact if:
      // 1. Auto-open is enabled
      // 2. No artifact is currently selected
      // 3. Sidebar is already showing artifacts view
      if (
        autoOpenEnabled &&
        artifactList.length > 0 &&
        !rightSidebar.currentArtifactId &&
        rightSidebar.isArtifactItemOpen
      ) {
        const firstArtifact = artifactList[0];
        setTimeout(() => {
          openArtifact(firstArtifact.id);
        }, 0);
      }
    },
    [setArtifactAndOpenSidebar, openArtifact],
  );

  const metadataHandler = useCallback(
    (metadata: any, state: StreamingState) => {
      const { setDialogue } = state;
      if (metadata?.assigned_message_id) {
        setDialogue((prevDialogue) => {
          // Find the most recent User message (with or without an ID)
          const lastUserIdx = prevDialogue.findLastIndex(
            (msg) => msg.type === "User",
          );
          if (lastUserIdx < 0) return prevDialogue;

          const lastUser = prevDialogue[lastUserIdx];

          // Only assign the ID if the User message doesn't already have one.
          // During continueConversation (tool interaction continuation), the
          // User message already has an ID and the backend's
          // assigned_message_id may point to the *Assistant* message.
          // Overwriting would corrupt the User message's identity, and a
          // prompt sent mid-turn must not take an id another message holds.
          if (
            lastUser.id ||
            prevDialogue.some((msg) => msg.id === metadata.assigned_message_id)
          )
            return prevDialogue;

          const updated = [...prevDialogue];
          updated[lastUserIdx] = {
            ...lastUser,
            id: metadata.assigned_message_id,
          };
          return updated;
        });
      }

      // Sources + live-searched routing lives in the SHARED helper — this
      // handler and the reusable-chat one must not drift (#20423).
      // Auto-open on sources arrival is handled centrally by AASidebar to
      // avoid race conditions between sources and artifacts tabs.
      applySourcesMetadata(metadata, state);

      if (metadata && "routing" in metadata) {
        const store = useChatStore.getState();
        // Falsy check on persona_slug: backend sends empty string or null
        // when routing cleared (e.g. after access-denied follow-up).
        if (metadata.routing?.persona_slug) {
          store.setRouting(metadata.routing);
          // Don't clear persona — getActivePersonaIdentity() prefers routing
          // over persona, so the routing chip shows the right name.
          // Persona slice stays for operational data (systemPrompt, modelCapabilities).
        } else {
          // Backend sent empty persona_slug — clear routing state so the
          // chip disappears (e.g. after access-denied follow-up).
          store.setRouting(null);
        }
      }

      // #30968 banner. Of the metadata events on this type only sources events
      // lack assigned_message_id, so they never touch it. Every turn-initial
      // event (first turn, tool continuation, routing short-circuit) re-applies
      // it: key present re-affirms, key absent clears, whether access was
      // restored or that path never computed it.
      if (metadata && "assigned_message_id" in metadata) {
        const access = metadata.agent_dataset_access;
        useChatStore
          .getState()
          .setAgentDatasetAccess(
            access
              ? { ...access, dialogue_id: metadata.dialogue_id ?? null }
              : null,
          );
      }

      // FTUX onboarding module tracking
      if (metadata?.type === "onboarding_module") {
        const onboardingStore = useOnboardingStore.getState();
        onboardingStore.setCurrentModule(metadata.module_id);
        onboardingStore.setCurrentModuleIndex(metadata.index ?? 0);
        onboardingStore.setTotalModules(metadata.total ?? 7);
      }

      // FTUX onboarding completion — wipe the store so profile form,
      // artifacts, and module state from this run don't bleed into the
      // next one, then route the user to the destination they picked in
      // module 7. API `/user/onboarding-status` remains the source of
      // truth for completion; this just updates in-session UI state.
      //
      // Record the finalized dialogue id BEFORE resetting so the
      // AtomAssistPage sync effect can skip re-asserting isOnboarding
      // during the brief window before the ChatStore persona slug
      // flips away from ftux-onboarding. Also guard history.push with
      // the current pathname so we don't yank a user who navigated
      // elsewhere into a different page.
      if (metadata?.type === "onboarding_complete") {
        const onboardingStore = useOnboardingStore.getState();
        const completedDialogueId = onboardingStore.onboardingDialogueId;
        onboardingStore.setFinalizedDialogueId(completedDialogueId);
        onboardingStore.resetOnboarding();

        const nextAction = metadata.next_action as string | null | undefined;
        const destination =
          nextAction && ONBOARDING_NEXT_ACTION_ROUTES[nextAction];
        if (destination) {
          // Only navigate if the user is still on an /assistant route —
          // an SSE event that fires while they've moved to another page
          // shouldn't yank them back.
          const currentPath =
            typeof window !== "undefined" ? window.location.pathname : "";
          if (currentPath.startsWith("/assistant")) {
            history.push(destination);
          }
        }
      }

      // FTUX onboarding artifacts — route to OnboardingStore for the
      // Verification Pane instead of the generic artifacts collection.
      if (metadata?.type === "artifact" && metadata?.artifact) {
        const artifact = metadata.artifact;
        const onboardingStore = useOnboardingStore.getState();
        if (artifact.type === "onboarding_profile") {
          onboardingStore.setProfileArtifact(artifact);
        } else if (artifact.type === "answer_details") {
          onboardingStore.setAnswerDetails(artifact);
        }
      }
    },
    [history],
  );

  const messageHandler = useCallback(
    (
      messageObject: IncomingSSEMessage,
      { artifacts, setDialogue, parseIncomingChunk } = {} as StreamingState,
    ) => {
      // Guard: some SSE events (e.g. during transport retries) may arrive
      // without a `content` field, which would crash the nested destructure.
      if (!messageObject?.content) return;

      const {
        id: messageId,
        parent_id,
        type: messageType,
        ranking_type,
        feedback_value,
        content: { text: incomingChunk, content_parts: incomingContentParts },
        content_type,
      } = messageObject;

      // Drop structurally empty chunks (no type, no content, no id).
      // They occasionally slip through and cascade-break the assistant
      // turn into multiple bubbles by becoming a typeless
      // ``lastMessage`` that fails ``shouldAppend``. Nothing renders
      // for them, so dropping is safe.
      if (
        !messageType &&
        !content_type &&
        !messageId &&
        !(incomingChunk && incomingChunk.length)
      ) {
        return;
      }

      setDialogue((prevDialogue) => {
        const lastMessage = prevDialogue[prevDialogue.length - 1];

        // Append only when the incoming chunk belongs to the same assistant
        // turn. A newer assistant message id indicates a new turn and must not
        // be glued onto the previous assistant reply.
        const shouldAppend =
          lastMessage &&
          lastMessage.type === "Assistant" &&
          (messageType === "Assistant" || messageType === "Tool") &&
          (!messageId || !lastMessage.id || lastMessage.id === messageId);

        // Prefer structured content_parts from persisted multi-part messages
        // (e.g. DB replay on retryConnection). These already have proper types.
        let parts: TMessageContentPart[];
        if (
          incomingContentParts &&
          Array.isArray(incomingContentParts) &&
          incomingContentParts.length > 0
        ) {
          parts = incomingContentParts
            .map((part: any) => {
              const text =
                typeof part?.text === "string"
                  ? part.text
                  : String(part?.text ?? "");
              const resolvedType = resolveContentType(part?.type, text);
              const fallbackType =
                messageType === "Tool"
                  ? ContentType.TOOL_RESPONSE
                  : ContentType.TEXT;
              return { type: resolvedType || fallbackType, text };
            })
            .filter(
              (part: TMessageContentPart) =>
                part.text !== "" ||
                part.type === ContentType.TOOL_CALL ||
                part.type === ContentType.TOOL_RESPONSE,
            );
        } else if (content_type) {
          parts = incomingChunk
            ? [{ type: content_type, text: incomingChunk }]
            : [];
        } else {
          // Resolve content type from explicit label or payload shape so native
          // JSON tool chunks are routed to tool UI instead of plain text.
          const resolvedType = resolveContentType(content_type, incomingChunk);
          const fallbackToolType =
            messageType === "Tool" ? ContentType.TOOL_RESPONSE : null;
          const effectiveType = resolvedType || fallbackToolType;

          if (effectiveType) {
            parts = incomingChunk
              ? [{ type: effectiveType, text: incomingChunk }]
              : [];
          } else {
            // Legacy fallback: parse XML tags from text
            const parsingStateId =
              shouldAppend && lastMessage.id ? lastMessage.id : messageId;
            parts = parseIncomingChunk(incomingChunk, parsingStateId);
          }
        }

        // Protocol enforcement: re-classify any TEXT parts that are actually
        // JSON tool payloads.  This is the last line of defense before rendering.
        parts = normalizeContentParts(parts);

        if (shouldAppend) {
          const updatedMessage: ChatMessageWithParts = {
            ...lastMessage,
            contentParts: lastMessage.contentParts
              ? [...lastMessage.contentParts]
              : [],
          };

          parts.forEach((part) => {
            // Parallel batches interleave stubs/finals, so merge by id.
            if (
              part.type === ContentType.TOOL_CALL ||
              part.type === ContentType.TOOL_RESPONSE
            ) {
              const partId = extractToolCallId(part.text);
              const ownIndex = partId
                ? updatedMessage.contentParts.findIndex(
                    (p) =>
                      p.type === part.type &&
                      extractToolCallId(p.text) === partId,
                  )
                : -1;
              if (ownIndex >= 0) {
                updatedMessage.contentParts[ownIndex] = {
                  ...updatedMessage.contentParts[ownIndex],
                  text: updatedMessage.contentParts[ownIndex].text + part.text,
                };
                return;
              }
            }
            const lastIndex = updatedMessage.contentParts.length - 1;
            const lastPart =
              lastIndex >= 0 ? updatedMessage.contentParts[lastIndex] : null;
            if (lastPart && canMergeConsecutiveParts(lastPart, part)) {
              updatedMessage.contentParts[lastIndex] = {
                ...lastPart,
                text: lastPart.text + part.text,
              };
            } else {
              updatedMessage.contentParts.push(part);
            }
          });
          return [
            ...prevDialogue.slice(0, prevDialogue.length - 1),
            updatedMessage,
          ];
        } else {
          if (isDuplicateUserMessage(prevDialogue, messageType, messageId)) {
            return prevDialogue;
          }
          const newMessage: ChatMessageWithParts = {
            id: messageId,
            parent_id,
            type: messageType,
            ranking_type,
            feedback_value: feedback_value || null,
            contentParts: parts,
            // Snapshot the current routing state for per-message labels.
            // The SSE metadata event always arrives before the first chunk,
            // so useChatStore.routing is already up-to-date here.
            ...(messageType === "Assistant" && {
              routing: useChatStore.getState().routing ?? undefined,
            }),
          };

          return [...prevDialogue, newMessage];
        }
      });

      setTimeout(() => {
        checkForArtifacts(artifacts);
      }, 100);
    },
    [checkForArtifacts],
  );

  return {
    metadataHandler,
    messageHandler,
  };
};

export default useAtomAssistEventHandlers;
